/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================================
   อ่านใบส่งของด้วย AI → เติมใบรับสินค้า  (หน้าลับ ยังไม่มีในเมนู)
   เข้าได้ที่  /ERP/goods-receipt/ai-scan

   1) ถ่ายรูป/เลือกไฟล์ใบส่งของ  → ระบบย่อรูปแล้วส่งให้ AI อ่าน
   2) เทียบรูปกับสิ่งที่อ่านได้ แก้ผู้ขาย/รายการ/จำนวน/วันหมดอายุได้ทุกช่อง
   3) กด "สร้างใบรับสินค้า" → ไปหน้าสร้างใบรับสินค้าเดิม พร้อมข้อมูลที่เติมไว้แล้ว
      (หน้านี้ไม่บันทึกใบรับสินค้า ไม่แตะสต๊อก — ผู้ใช้กดบันทึกที่หน้าใบรับสินค้าเอง)
   ============================================================================ */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Empty,
  Flex,
  Image,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from "antd";
import { InboxOutlined, ReloadOutlined, SwapOutlined } from "@ant-design/icons";
import { TbSquareRoundedCheck } from "react-icons/tb";
import dayjs from "dayjs";

import AiDocScanService from "../../../service/ERP/AiDocScan.service";
import ModalSupplier from "../../../components/modal/ERP/supplier/ModalSupplier";
import { ButtonBack } from "../../../components/button";
import { formatMoney } from "../../../utils/util";

const svc = AiDocScanService();
const gotoFrom = "/ERP/goods-receipt";
const GR_CREATE = "/ERP/goods-receipt/manage/create";
const dateFormat = "DD/MM/YYYY";
const MAX_SIDE = 2000;

/* ---------------------------------------------------------------- helpers */

const num = (v, d = 2) => formatMoney(Number(v || 0), d);

/* หน่วยกลาง ให้ตรงกับฝั่ง PHP (AiText::unit) */
const UNITS = {
  kg: ["kg", "kgs", "กก", "กิโลกรัม", "กิโล", "kilogram", "kilograms"],
  g: ["g", "gm", "gram", "grams", "กรัม", "ก"],
  ton: ["ton", "tons", "ตัน", "mt"],
  l: ["l", "lt", "ltr", "litre", "liter", "ลิตร", "ล"],
  ml: ["ml", "มล", "มิลลิลิตร", "cc", "ซีซี"],
  bag: ["bag", "bags", "ถุง", "กระสอบ", "sack", "sacks"],
  box: ["box", "boxes", "กล่อง"],
  carton: ["carton", "cartons", "ctn", "ctns", "ลัง"],
  bottle: ["bottle", "bottles", "btl", "ขวด"],
  can: ["can", "cans", "กระป๋อง"],
  drum: ["drum", "drums", "ถัง", "แกลลอน", "gallon", "pail", "pails"],
  pcs: ["pc", "pcs", "piece", "pieces", "ea", "each", "ชิ้น", "อัน", "unit", "units"],
  roll: ["roll", "rolls", "ม้วน"],
  pack: ["pack", "packs", "pk", "แพ็ค", "แพค", "แพ็ก", "ห่อ"],
  set: ["set", "sets", "ชุด"],
  sheet: ["sheet", "sheets", "แผ่น"],
  m: ["m", "meter", "metre", "เมตร"],
};
const normText = (s) => String(s || "").toLowerCase().replace(/[\s\p{P}\p{S}]+/gu, "");
const unitKey = (u) => {
  const n = normText(u);
  if (!n) return "";
  for (const [k, alts] of Object.entries(UNITS)) if (alts.some((a) => normText(a) === n)) return k;
  return n;
};

/* ย่อรูปในเครื่องก่อนส่ง: ด้านยาวไม่เกิน 2000px, JPG 85% — ส่งเร็ว ประหยัดค่า AI */
function loadViaImg(raw) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(raw);
    const img = new window.Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("เบราว์เซอร์เปิดรูปนี้ไม่ได้ — ถ้าเป็นไฟล์ HEIC จาก iPhone ให้ส่งเป็น JPG"));
    };
    img.src = url;
  });
}

async function prepareFile(raw) {
  const isPdf = raw.type === "application/pdf" || /\.pdf$/i.test(raw.name);
  if (isPdf) return { file: raw, pdf: true };
  if (!raw.type.startsWith("image/") && !/\.(jpe?g|png|webp|heic|heif)$/i.test(raw.name)) {
    throw new Error("รองรับเฉพาะรูปภาพ หรือไฟล์ PDF");
  }
  let bmp;
  try {
    bmp = await createImageBitmap(raw, { imageOrientation: "from-image" });
  } catch (e) {
    bmp = await loadViaImg(raw);
  }
  const bw = bmp.width || bmp.naturalWidth;
  const bh = bmp.height || bmp.naturalHeight;
  const scale = Math.min(1, MAX_SIDE / Math.max(bw, bh));
  const w = Math.max(1, Math.round(bw * scale));
  const h = Math.max(1, Math.round(bh * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, w, h);
  ctx.drawImage(bmp, 0, 0, w, h);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.85));
  if (!blob) throw new Error("แปลงรูปไม่สำเร็จ ลองเลือกไฟล์ใหม่");
  const name = (raw.name || "scan").replace(/\.[^.]+$/, "") + ".jpg";
  return { file: new File([blob], name, { type: "image/jpeg" }), pdf: false };
}

const SUPPLIER_SOURCE = {
  tax_id: { color: "success", text: "เลขผู้เสียภาษีตรงกัน" },
  alias: { color: "success", text: "เคยยืนยันชื่อนี้แล้ว" },
  name: { color: "warning", text: "ชื่อใกล้เคียง" },
  manual: { color: "blue", text: "เลือกเอง" },
};

/* ================================================================== page */

function GoodsReceiptAiScan() {
  const navigate = useNavigate();

  const [preview, setPreview] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyText, setBusyText] = useState("");
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);

  const [result, setResult] = useState(null);
  const [supplier, setSupplier] = useState(null);
  const [supplierSource, setSupplierSource] = useState(null);
  const [invNo, setInvNo] = useState("");
  const [invDate, setInvDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [openSupplier, setOpenSupplier] = useState(false);

  const timerRef = useRef(null);
  const previewRef = useRef(null);

  useEffect(() => {
    return () => {
      clearInterval(timerRef.current);
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    };
  }, []);

  const poMap = useMemo(
    () => new Map((result?.po_lines || []).map((p) => [String(p.code), p])),
    [result]
  );

  /* ผลรวมจำนวนต่อบรรทัด PO (หลายแถวในใบส่งของอาจเป็นสินค้าตัวเดียวกันคนละล็อต) */
  const groupQty = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!r.include || !r.chosen) return;
      m.set(r.chosen, (m.get(r.chosen) || 0) + Number(r.qty || 0));
    });
    return m;
  }, [rows]);

  const groupCount = useMemo(() => {
    const m = new Map();
    rows.forEach((r) => {
      if (!r.include || !r.chosen) return;
      m.set(r.chosen, (m.get(r.chosen) || 0) + 1);
    });
    return m;
  }, [rows]);

  /* ---------------------------------------------------------- actions */

  const startTimer = (text) => {
    setBusy(true);
    setBusyText(text);
    setElapsed(0);
    const t0 = Date.now();
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsed(Math.round((Date.now() - t0) / 1000)), 500);
  };
  const stopTimer = () => {
    clearInterval(timerRef.current);
    setBusy(false);
  };

  const applyResult = (data, keepHeader = false) => {
    setResult(data);
    setSupplier(data?.supplier?.matched || null);
    setSupplierSource(data?.supplier?.matched ? data?.supplier?.source : null);
    if (!keepHeader) {
      setInvNo(data?.document?.document_no || "");
      setInvDate(data?.document?.document_date ? dayjs(data.document.document_date) : null);
    }
    setRows(
      (data?.lines || []).map((l) => ({
        key: l.index,
        doc: l.doc,
        match: l.match,
        suggest: l.suggest_master || [],
        chosen: l.match?.code != null ? String(l.match.code) : null,
        qty: l.doc?.qty ?? null,
        mfg: l.doc?.mfg_date ? dayjs(l.doc.mfg_date) : null,
        exp: l.doc?.exp_date ? dayjs(l.doc.exp_date) : null,
        include: l.match?.code != null,
      }))
    );
  };

  const handleFile = async (raw) => {
    setError(null);
    setResult(null);
    setRows([]);
    setSupplier(null);
    let prepared;
    try {
      startTimer("กำลังเตรียมรูป…");
      prepared = await prepareFile(raw);
    } catch (e) {
      stopTimer();
      setError(e?.message || "เปิดไฟล์ไม่สำเร็จ");
      return false;
    }

    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    const url = prepared.pdf ? null : URL.createObjectURL(prepared.file);
    previewRef.current = url;
    setPreview(url);
    setIsPdf(prepared.pdf);

    setBusyText("AI กำลังอ่านใบส่งของ…");
    try {
      const res = await svc.scan(prepared.file);
      applyResult(res?.data?.data);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "อ่านใบส่งของไม่สำเร็จ");
    } finally {
      stopTimer();
    }
    return false; // ไม่ให้ Upload ของ antd ส่งไฟล์เอง
  };

  const handleSupplierPicked = async (v) => {
    if (!v?.supcode || !result?.scan_id) return;
    startTimer("กำลังจับคู่รายการกับ PO ของผู้ขายรายนี้…");
    try {
      const res = await svc.rematch(result.scan_id, v.supcode);
      applyResult(res?.data?.data, true);
      setSupplierSource("manual");
    } catch (err) {
      message.error(err?.response?.data?.message || "จับคู่รายการไม่สำเร็จ");
    } finally {
      stopTimer();
    }
  };

  const updateRow = (key, patch) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const resetAll = () => {
    if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    previewRef.current = null;
    setPreview(null);
    setIsPdf(false);
    setResult(null);
    setRows([]);
    setSupplier(null);
    setSupplierSource(null);
    setInvNo("");
    setInvDate(null);
    setError(null);
  };

  const readyRows = rows.filter((r) => r.include && r.chosen && Number(r.qty) > 0);
  const needCheck = rows.filter(
    (r) => r.include && r.chosen && String(r.match?.code) === r.chosen && r.match?.level !== "high"
  ).length;
  const unmatched = rows.filter((r) => !r.chosen).length;

  const handleSend = () => {
    if (!supplier) return message.warning("กรุณาเลือกผู้ขายก่อน");
    if (!readyRows.length) return message.warning("ยังไม่มีรายการที่จับคู่กับ PO และมีจำนวนรับ");

    const groups = new Map();
    readyRows.forEach((r) => groups.set(r.chosen, [...(groups.get(r.chosen) || []), r]));

    const items = [...groups.entries()].map(([code, g]) => {
      const p = poMap.get(code);
      return {
        code: p.code,
        pocode: p.pocode,
        podate: p.podate,
        stcode: p.stcode,
        stname: p.stname,
        typename: p.typename,
        price: Number(p.price || 0),
        qty_buy: Number(p.qty || 0),
        recamount: Number(p.recamount || 0),
        qty: g.reduce((a, r) => a + Number(r.qty || 0), 0),
        discount: Number(p.discount || 0),
        unit: p.unit,
        shelf_life: p.shelf_life,
        /* แต่ละแถวในใบส่งของ = 1 ล็อต (วันผลิต/วันหมดอายุของตัวเอง) */
        lots: g.map((r) => ({
          qty: Number(r.qty || 0),
          mfg_date: r.mfg ? r.mfg.format("YYYY-MM-DD") : null,
          expire_date: r.exp ? r.exp.format("YYYY-MM-DD") : null,
        })),
      };
    });

    const supplierLots = readyRows
      .filter((r) => r.doc?.lot_no)
      .map((r) => `${poMap.get(r.chosen)?.stname}: ${r.doc.lot_no}`);
    const remark = [
      `อ่านจากใบส่งของด้วย AI #${result.scan_id}`,
      supplierLots.length ? `Lot ผู้ขาย ${supplierLots.join(", ")}` : null,
    ]
      .filter(Boolean)
      .join(" · ")
      .slice(0, 500);

    /* จำชื่อที่ผู้ใช้ยืนยันแล้ว — ครั้งหน้าจับคู่ได้ทันที (ไม่รอผล ไม่ขวางการทำงาน) */
    const rememberSupplier = supplierSource && supplierSource !== "tax_id" && supplierSource !== "alias";
    svc
      .saveAlias({
        supcode: supplier.supcode,
        supplier_raw: rememberSupplier ? result?.document?.supplier?.name || null : null,
        items: readyRows.map((r) => ({ raw_text: r.doc?.name, stcode: poMap.get(r.chosen)?.stcode })),
      })
      .catch(() => {});

    navigate(GR_CREATE, {
      state: {
        config: { title: "สร้างใบรับสินค้า", textOk: null, textCancel: null, action: "create", code: null },
        aiPrefill: {
          scan_id: result.scan_id,
          supplier,
          inv_no: (invNo || "").trim() || null,
          inv_date: invDate ? invDate.format("YYYY-MM-DD") : null,
          remark,
          items,
        },
      },
    });
  };

  /* ------------------------------------------------------- render bits */

  const optionsFor = (row) => {
    const candScore = new Map((row.match?.candidates || []).map((c) => [String(c.code), c.score]));
    const toOpt = (p) => ({
      value: String(p.code),
      label: `${p.pocode} · ${p.stname} (ค้าง ${num(p.remain_qty)} ${p.unit || ""})`,
    });
    const rec = [...candScore.entries()]
      .filter(([, s]) => s >= 0.3)
      .map(([c]) => poMap.get(c))
      .filter(Boolean);
    const recSet = new Set(rec.map((p) => String(p.code)));
    const rest = [...poMap.values()].filter((p) => !recSet.has(String(p.code)));
    const out = [];
    if (rec.length) out.push({ label: "แนะนำจากชื่อที่อ่านได้", options: rec.map(toOpt) });
    if (rest.length) out.push({ label: "รายการค้างรับอื่นของผู้ขายนี้", options: rest.map(toOpt) });
    return out;
  };

  const MatchTag = ({ row }) => {
    if (!row.chosen) return <Tag color="error">ยังไม่จับคู่</Tag>;
    if (String(row.match?.code) !== row.chosen) return <Tag color="blue">เลือกเอง</Tag>;
    const pct = Math.round((row.match?.score || 0) * 100);
    if (row.match?.source === "alias")
      return (
        <Tooltip title="เคยยืนยันชื่อนี้กับผู้ขายรายนี้แล้ว">
          <Tag color="success">จำไว้แล้ว</Tag>
        </Tooltip>
      );
    if (row.match?.source === "ai")
      return (
        <Tooltip title={row.match?.reason || "AI จับคู่จากความหมายของชื่อ"}>
          <Tag color={row.match?.level === "high" ? "success" : "warning"}>AI จับคู่</Tag>
        </Tooltip>
      );
    if (row.match?.source === "code") return <Tag color="success">รหัสตรงกัน</Tag>;
    return row.match?.level === "high" ? (
      <Tag color="success">ตรงกัน {pct}%</Tag>
    ) : (
      <Tag color="warning">ตรวจสอบ {pct}%</Tag>
    );
  };

  const rowWarnings = (row) => {
    const w = [];
    const p = row.chosen ? poMap.get(row.chosen) : null;
    if (!p) return w;
    const total = groupQty.get(row.chosen) || 0;
    if (row.include && total > Number(p.remain_qty || 0) + 1e-9) {
      w.push(`รวมแล้วเกินยอดค้างรับ (ค้าง ${num(p.remain_qty)} ${p.unit || ""})`);
    }
    if (row.doc?.unit && p.unit && unitKey(row.doc.unit) !== unitKey(p.unit)) {
      w.push(`หน่วยในใบ "${row.doc.unit}" ไม่ตรงกับ PO "${p.unit}" — ตรวจการแปลงหน่วย`);
    }
    const docPrice = Number(row.doc?.unit_price);
    const poPrice = Number(p.price || 0);
    if (row.doc?.unit_price != null && poPrice > 0 && Math.abs(docPrice - poPrice) / poPrice > 0.005) {
      w.push(`ราคาในใบ ${num(docPrice)} ไม่ตรงกับ PO ${num(poPrice)} (ใบรับสินค้าใช้ราคา PO)`);
    }
    return w;
  };

  const columns = [
    {
      title: "ใช้",
      dataIndex: "include",
      width: 52,
      align: "center",
      render: (_, r) => (
        <Checkbox
          checked={r.include}
          disabled={!r.chosen}
          onChange={(e) => updateRow(r.key, { include: e.target.checked })}
        />
      ),
    },
    {
      title: "ที่อ่านได้จากใบส่งของ",
      dataIndex: "doc",
      width: 280,
      render: (_, r) => (
        <Space direction="vertical" size={2} style={{ width: "100%" }}>
          <Typography.Text strong>{r.doc?.name}</Typography.Text>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {r.doc?.code ? `รหัสผู้ขาย ${r.doc.code} · ` : ""}
            {r.doc?.qty != null ? `${num(r.doc.qty)} ${r.doc?.unit || ""}` : "ไม่พบจำนวน"}
            {r.doc?.unit_price != null ? ` · @${num(r.doc.unit_price)}` : ""}
          </Typography.Text>
          {(r.doc?.lot_no || r.doc?.mfg_date || r.doc?.exp_date) && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {r.doc?.lot_no ? `Lot ${r.doc.lot_no}` : ""}
              {r.doc?.mfg_date ? ` · MFG ${dayjs(r.doc.mfg_date).format(dateFormat)}` : ""}
              {r.doc?.exp_date ? ` · EXP ${dayjs(r.doc.exp_date).format(dateFormat)}` : ""}
            </Typography.Text>
          )}
          {r.doc?.confidence === "low" && <Tag color="orange">อ่านไม่ชัด ตรวจกับรูป</Tag>}
        </Space>
      ),
    },
    {
      title: "จับคู่กับรายการ PO ที่ค้างรับ",
      dataIndex: "chosen",
      width: 360,
      render: (_, r) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <Select
            value={r.chosen}
            placeholder={poMap.size ? "เลือกรายการ PO" : "ไม่มี PO ค้างรับของผู้ขายนี้"}
            options={optionsFor(r)}
            showSearch
            allowClear
            optionFilterProp="label"
            style={{ width: "100%" }}
            disabled={!poMap.size}
            onChange={(v) => updateRow(r.key, { chosen: v ?? null, include: v != null })}
          />
          <Flex gap={4} wrap="wrap" align="center">
            <MatchTag row={r} />
            {r.include && r.chosen && (groupCount.get(r.chosen) || 0) > 1 && (
              <Tooltip title="หลายแถวในใบส่งของเป็นสินค้าตัวเดียวกัน จะรวมเป็นรายการเดียวและแยกเป็นหลายล็อต">
                <Tag>แยกล็อต</Tag>
              </Tooltip>
            )}
          </Flex>
          {!r.chosen && r.suggest?.length > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              ในระบบน่าจะเป็น {r.suggest[0].stcode} {r.suggest[0].stname} — แต่ไม่มีใน PO ค้างรับของผู้ขายนี้
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "จำนวนรับ",
      dataIndex: "qty",
      width: 170,
      render: (_, r) => {
        const p = r.chosen ? poMap.get(r.chosen) : null;
        const warns = rowWarnings(r);
        return (
          <Space direction="vertical" size={4} style={{ width: "100%" }}>
            <InputNumber
              value={r.qty}
              min={0}
              style={{ width: "100%" }}
              addonAfter={p?.unit || r.doc?.unit || ""}
              onChange={(v) => updateRow(r.key, { qty: v })}
            />
            {warns.map((w) => (
              <Typography.Text key={w} type="warning" style={{ fontSize: 12, display: "block" }}>
                {w}
              </Typography.Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: "วันผลิต / วันหมดอายุ",
      dataIndex: "dates",
      width: 170,
      render: (_, r) => (
        <Space direction="vertical" size={4} style={{ width: "100%" }}>
          <DatePicker
            value={r.mfg}
            format={dateFormat}
            placeholder="วันผลิต"
            style={{ width: "100%" }}
            onChange={(v) => updateRow(r.key, { mfg: v })}
          />
          <DatePicker
            value={r.exp}
            format={dateFormat}
            placeholder="วันหมดอายุ"
            style={{ width: "100%" }}
            onChange={(v) => updateRow(r.key, { exp: v })}
          />
        </Space>
      ),
    },
  ];

  const doc = result?.document;
  const srcInfo = SUPPLIER_SOURCE[supplierSource] || null;

  const SupplierBlock = supplier ? (
    <Flex justify="space-between" align="start" gap={8} wrap="wrap">
      <Space direction="vertical" size={2}>
        <Typography.Text strong style={{ fontSize: 16 }}>
          {supplier.supname}
        </Typography.Text>
        <Typography.Text type="secondary">
          {supplier.supcode}
          {supplier.taxnumber ? ` · เลขผู้เสียภาษี ${supplier.taxnumber}` : ""}
        </Typography.Text>
        {srcInfo && (
          <Tag color={srcInfo.color}>
            {srcInfo.text}
            {supplierSource === "name" ? ` ${Math.round((result?.supplier?.score || 0) * 100)}%` : ""}
          </Tag>
        )}
      </Space>
      <Button icon={<SwapOutlined />} onClick={() => setOpenSupplier(true)}>
        เปลี่ยนผู้ขาย
      </Button>
    </Flex>
  ) : (
    <Space direction="vertical" size={8} style={{ width: "100%" }}>
      <Alert
        type="warning"
        showIcon
        message="หาผู้ขายในระบบไม่เจอ"
        description={
          doc?.supplier?.name
            ? `ในใบเขียนว่า "${doc.supplier.name}"${doc?.supplier?.tax_id ? ` เลขผู้เสียภาษี ${doc.supplier.tax_id}` : ""}`
            : "อ่านชื่อผู้ขายจากใบไม่ได้"
        }
      />
      {(result?.supplier?.candidates || []).length > 0 && (
        <Flex gap={6} wrap="wrap">
          <Typography.Text type="secondary">ใกล้เคียง:</Typography.Text>
          {result.supplier.candidates.map((c) => (
            <Button key={c.supcode} size="small" onClick={() => handleSupplierPicked(c)}>
              {c.supname} ({Math.round(c.score * 100)}%)
            </Button>
          ))}
        </Flex>
      )}
      <Button type="primary" ghost onClick={() => setOpenSupplier(true)}>
        เลือกผู้ขาย
      </Button>
    </Space>
  );

  /* ------------------------------------------------------------ layout */

  return (
    <div className="goods-receipt-ai-scan" style={{ paddingBottom: 24 }}>
      <Flex justify="space-between" align="center" className="mb-3" wrap="wrap" gap={8}>
        <Space direction="vertical" size={0}>
          <Typography.Title level={3} className="m-0 !text-zinc-800">
            อ่านใบส่งของด้วย AI
          </Typography.Title>
          <Typography.Text type="secondary">
            ถ่ายรูปใบส่งของ → ตรวจที่ระบบอ่านได้ → ส่งไปสร้างใบรับสินค้า
          </Typography.Text>
        </Space>
        <Space>
          {result && (
            <Button icon={<ReloadOutlined />} onClick={resetAll} disabled={busy}>
              สแกนใบใหม่
            </Button>
          )}
          <ButtonBack target={gotoFrom} />
        </Space>
      </Flex>

      {result?.mock && (
        <Alert
          className="mb-3"
          type="info"
          showIcon
          message="โหมดจำลอง (AI_MOCK=1)"
          description="ข้อมูลชุดนี้สร้างจาก PO ค้างรับในระบบ ไม่ได้อ่านจากรูปจริง ใช้ทดสอบขั้นตอนเท่านั้น"
        />
      )}

      <Row gutter={[16, 16]}>
        {/* ---------------- รูปต้นฉบับ ---------------- */}
        <Col xs={24} lg={9}>
          <Card title="ใบส่งของ" size="small" style={{ position: "sticky", top: 12 }}>
            {!preview && !isPdf ? (
              <Upload.Dragger
                accept="image/*,application/pdf"
                multiple={false}
                showUploadList={false}
                beforeUpload={handleFile}
                disabled={busy}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">แตะเพื่อถ่ายรูป หรือลากไฟล์มาวาง</p>
                <p className="ant-upload-hint">รูป JPG / PNG หรือ PDF · ถ่ายให้เห็นทั้งใบ ไม่เอียงมาก</p>
              </Upload.Dragger>
            ) : (
              <Space direction="vertical" style={{ width: "100%" }}>
                {preview ? (
                  <Image src={preview} alt="ใบส่งของ" style={{ width: "100%", borderRadius: 6 }} />
                ) : (
                  <Empty description="ไฟล์ PDF — ดูต้นฉบับจากไฟล์ในเครื่อง" />
                )}
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  กดที่รูปเพื่อขยาย เทียบกับข้อมูลด้านขวาได้
                </Typography.Text>
              </Space>
            )}
          </Card>
        </Col>

        {/* ---------------- ผลที่อ่านได้ ---------------- */}
        <Col xs={24} lg={15}>
          <Spin spinning={busy} tip={`${busyText} ${elapsed ? `${elapsed} วิ` : ""}`}>
            {error && (
              <Alert
                className="mb-3"
                type="error"
                showIcon
                message="อ่านใบส่งของไม่สำเร็จ"
                description={error}
                action={
                  <Button size="small" onClick={resetAll}>
                    ลองใหม่
                  </Button>
                }
              />
            )}

            {!result && !busy && !error && (
              <Card size="small">
                <Empty description="ยังไม่มีข้อมูล — เลือกรูปใบส่งของทางซ้าย" />
              </Card>
            )}

            {result && (
              <Space direction="vertical" size={16} style={{ width: "100%" }}>
                <Card size="small" title="หัวเอกสาร">
                  <Row gutter={[16, 12]}>
                    <Col xs={24} md={14}>
                      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 4 }}>
                        ผู้ขาย
                      </Typography.Text>
                      {SupplierBlock}
                    </Col>
                    <Col xs={24} md={10}>
                      <Space direction="vertical" size={8} style={{ width: "100%" }}>
                        <div>
                          <Typography.Text type="secondary">เลขที่ใบส่งของ / ใบกำกับ</Typography.Text>
                          <Input value={invNo} onChange={(e) => setInvNo(e.target.value)} placeholder="ไม่พบในใบ" />
                        </div>
                        <div>
                          <Typography.Text type="secondary">วันที่ในเอกสาร</Typography.Text>
                          <DatePicker
                            value={invDate}
                            onChange={setInvDate}
                            format={dateFormat}
                            style={{ width: "100%" }}
                          />
                        </div>
                        {doc?.po_reference && (
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            ในใบอ้างถึง PO {doc.po_reference}{" "}
                            {(result?.po_reference_found || []).length > 0 ? (
                              <Tag color="success">พบใน PO ค้างรับ</Tag>
                            ) : (
                              <Tag>ไม่พบใน PO ค้างรับ</Tag>
                            )}
                          </Typography.Text>
                        )}
                      </Space>
                    </Col>
                  </Row>
                  {doc?.notes && <Alert className="mt-3" type="info" showIcon message={doc.notes} />}
                </Card>

                <Card
                  size="small"
                  title={`รายการในใบส่งของ (${rows.length})`}
                  extra={
                    doc?.grand_total != null ? (
                      <Typography.Text type="secondary">ยอดรวมในใบ {num(doc.grand_total)}</Typography.Text>
                    ) : null
                  }
                >
                  <Table
                    rowKey="key"
                    size="small"
                    columns={columns}
                    dataSource={rows}
                    pagination={false}
                    scroll={{ x: 1030 }}
                    rowClassName={(r) => (!r.include ? "opacity-60" : "")}
                    locale={{ emptyText: "อ่านรายการสินค้าจากใบไม่ได้" }}
                  />
                </Card>

                <Card size="small">
                  <Flex justify="space-between" align="center" gap={12} wrap="wrap">
                    <Space direction="vertical" size={0}>
                      <Typography.Text>
                        พร้อมส่ง <b>{readyRows.length}</b> แถว
                        {needCheck > 0 && (
                          <>
                            {" "}
                            · <Typography.Text type="warning">ควรตรวจ {needCheck}</Typography.Text>
                          </>
                        )}
                        {unmatched > 0 && (
                          <>
                            {" "}
                            · <Typography.Text type="danger">ยังไม่จับคู่ {unmatched}</Typography.Text>
                          </>
                        )}
                      </Typography.Text>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        ยังไม่บันทึกอะไร — ไปตรวจและกดบันทึกที่หน้าใบรับสินค้าอีกครั้ง
                        {result?.usage?.ms ? ` · อ่าน ${(result.usage.ms / 1000).toFixed(1)} วิ` : ""}
                      </Typography.Text>
                    </Space>
                    <Button
                      type="primary"
                      size="large"
                      icon={<TbSquareRoundedCheck style={{ fontSize: "1.1rem" }} />}
                      className="bn-center"
                      disabled={!supplier || readyRows.length === 0 || busy}
                      onClick={handleSend}
                    >
                      สร้างใบรับสินค้า
                    </Button>
                  </Flex>
                </Card>
              </Space>
            )}
          </Spin>
        </Col>
      </Row>

      {openSupplier && (
        <ModalSupplier
          show={openSupplier}
          close={() => setOpenSupplier(false)}
          values={(v) => handleSupplierPicked(v)}
          parm={{ pending: 1 }}
        />
      )}
    </div>
  );
}

export default GoodsReceiptAiScan;
